import { useState } from 'react'
import { useNotification } from '../contexts/NotificationContext'
import { formatDateForInput, validateAvailabilityForm } from '../utils/helpers'

function AvailabilityForm({ apiService }) {
  const [formData, setFormData] = useState({
    uvamId: '',
    capacityMW: '',
    startTime: formatDateForInput(new Date()),
    endTime: formatDateForInput(new Date(Date.now() + 3600000)), // +1 hour
    serviceType: 'mFRR',
    direction: 'upward',
    minPrice: '',
    maxPrice: '',
    rampRate: '',
    location: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { showNotification } = useNotification()

  const handleChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Check authentication first
    if (!apiService || !apiService.isAuthenticated()) {
      showNotification('Errore: utente non autenticato', 'error')
      return
    }

    // Validate form
    const validation = validateAvailabilityForm(formData)
    if (!validation.isValid) {
      showNotification(`Errore: ${validation.errors[0]}`, 'error')
      return
    }

    setIsSubmitting(true)
    try {
      const response = await apiService.createAvailability(formData)
      
      if (response.success) {
        showNotification('Disponibilità registrata con successo', 'success')
        // Reset form
        setFormData({
          uvamId: '',
          capacityMW: '',
          startTime: formatDateForInput(new Date()),
          endTime: formatDateForInput(new Date(Date.now() + 3600000)),
          serviceType: 'mFRR',
          direction: 'upward', 
          minPrice: '',
          maxPrice: '',
          rampRate: '',
          location: ''
        })
      } else {
        showNotification('Errore durante la registrazione', 'error')
      }
    } catch (error) {
      console.error('Availability submission failed:', error)
      showNotification(`Errore: ${error.message}`, 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="MuiPaper-root MuiCard-root" style={{ padding: '24px', margin: '16px 0' }}>
      <div className="MuiCardHeader-root" style={{ paddingBottom: '16px' }}>
        <div className="MuiCardHeader-content">
          <h2 className="MuiTypography-h5" style={{ margin: 0, color: '#1976d2' }}>
            Dichiara Disponibilità
          </h2>
          <p className="MuiTypography-body2" style={{ color: '#666', margin: '8px 0 0 0' }}>
            Registra la capacità disponibile per il mercato dei servizi di bilanciamento
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="MuiCardContent-root">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* UVAM ID Field */}
          <div className="MuiFormControl-root MuiTextField-root">
            <label 
              className="MuiInputLabel-root MuiInputLabel-formControl MuiInputLabel-animated" 
              htmlFor="uvam-id" 
              style={{ color: '#1976d2', fontWeight: 500 }}
            >
              UVAM ID *
            </label>
            <div className="MuiInputBase-root MuiOutlinedInput-root" style={{ marginTop: '8px' }}>
              <input 
                type="text" 
                id="uvam-id" 
                name="uvamId" 
                className="MuiInputBase-input MuiOutlinedInput-input"
                placeholder="es. UVAM001"
                pattern="[A-Z0-9]{3,10}"
                required
                value={formData.uvamId}
                onChange={handleChange('uvamId')}
                style={{ 
                  border: '1px solid #ccc', 
                  borderRadius: '4px', 
                  padding: '14px', 
                  width: '100%', 
                  boxSizing: 'border-box' 
                }}
              />
            </div>
            <div className="MuiFormHelperText-root" style={{ color: '#666', fontSize: '0.75rem', marginTop: '4px' }}>
              Formato: 3-10 caratteri alfanumerici maiuscoli
            </div>
          </div>

          {/* Two-column layout for capacity and times */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            {/* Capacity */}
            <div className="MuiFormControl-root MuiTextField-root">
              <label 
                className="MuiInputLabel-root"
                htmlFor="capacity"
                style={{ color: '#1976d2', fontWeight: 500 }}
              >
                Capacità (MW) *
              </label>
              <input 
                type="number" 
                id="capacity"
                name="capacityMW"
                className="MuiInputBase-input MuiOutlinedInput-input"
                placeholder="es. 50"
                min="0.1"
                step="0.1"
                required
                value={formData.capacityMW}
                onChange={handleChange('capacityMW')}
                style={{ 
                  border: '1px solid #ccc', 
                  borderRadius: '4px', 
                  padding: '14px', 
                  width: '100%',
                  marginTop: '8px' 
                }}
              />
            </div>

            {/* Service Type */}
            <div className="MuiFormControl-root">
              <label 
                className="MuiInputLabel-root"
                htmlFor="service-type"
                style={{ color: '#1976d2', fontWeight: 500 }}
              >
                Tipo Servizio
              </label>
              <select 
                id="service-type"
                name="serviceType"
                className="MuiInputBase-input MuiOutlinedInput-input"
                value={formData.serviceType}
                onChange={handleChange('serviceType')}
                style={{ 
                  border: '1px solid #ccc', 
                  borderRadius: '4px', 
                  padding: '14px', 
                  width: '100%',
                  marginTop: '8px' 
                }}
              >
                <option value="mFRR">mFRR - Manual Frequency Restoration Reserve</option>
                <option value="aFRR">aFRR - Automatic Frequency Restoration Reserve</option>
                <option value="FCR">FCR - Frequency Containment Reserve</option>
                <option value="RR">RR - Replacement Reserve</option>
              </select>
            </div>
          </div>

          {/* Time Range */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {/* Start Time */}
            <div className="MuiFormControl-root MuiTextField-root">
              <label 
                className="MuiInputLabel-root"
                htmlFor="start-time"
                style={{ color: '#1976d2', fontWeight: 500 }}
              >
                Inizio Disponibilità *
              </label>
              <input 
                type="datetime-local" 
                id="start-time"
                name="startTime"
                className="MuiInputBase-input MuiOutlinedInput-input"
                required
                value={formData.startTime}
                onChange={handleChange('startTime')}
                style={{ 
                  border: '1px solid #ccc', 
                  borderRadius: '4px', 
                  padding: '14px', 
                  width: '100%',
                  marginTop: '8px' 
                }}
              />
            </div>

            {/* End Time */}
            <div className="MuiFormControl-root MuiTextField-root">
              <label 
                className="MuiInputLabel-root"
                htmlFor="end-time"
                style={{ color: '#1976d2', fontWeight: 500 }}
              >
                Fine Disponibilità *
              </label>
              <input 
                type="datetime-local" 
                id="end-time"
                name="endTime"
                className="MuiInputBase-input MuiOutlinedInput-input"
                required
                value={formData.endTime}
                onChange={handleChange('endTime')}
                style={{ 
                  border: '1px solid #ccc', 
                  borderRadius: '4px', 
                  padding: '14px', 
                  width: '100%',
                  marginTop: '8px' 
                }}
              />
            </div>
          </div>

          {/* Direction and Location */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {/* Direction */}
            <div className="MuiFormControl-root">
              <label 
                className="MuiInputLabel-root"
                htmlFor="direction"
                style={{ color: '#1976d2', fontWeight: 500 }}
              >
                Direzione
              </label>
              <select 
                id="direction"
                name="direction"
                className="MuiInputBase-input MuiOutlinedInput-input"
                value={formData.direction}
                onChange={handleChange('direction')}
                style={{ 
                  border: '1px solid #ccc', 
                  borderRadius: '4px', 
                  padding: '14px', 
                  width: '100%',
                  marginTop: '8px' 
                }}
              >
                <option value="upward">Upward (Aumento potenza)</option>
                <option value="downward">Downward (Riduzione potenza)</option>
              </select>
            </div>

            {/* Location */}
            <div className="MuiFormControl-root MuiTextField-root">
              <label 
                className="MuiInputLabel-root"
                htmlFor="location"
                style={{ color: '#1976d2', fontWeight: 500 }}
              >
                Ubicazione
              </label>
              <input 
                type="text" 
                id="location"
                name="location"
                className="MuiInputBase-input MuiOutlinedInput-input"
                placeholder="es. Milano"
                value={formData.location}
                onChange={handleChange('location')}
                style={{ 
                  border: '1px solid #ccc', 
                  borderRadius: '4px', 
                  padding: '14px', 
                  width: '100%',
                  marginTop: '8px' 
                }}
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
            <button 
              type="submit"
              disabled={isSubmitting}
              className="MuiButton-root MuiButton-contained MuiButton-containedPrimary"
              style={{ 
                backgroundColor: isSubmitting ? '#ccc' : '#1976d2', 
                color: 'white', 
                padding: '12px 24px', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: isSubmitting ? 'not-allowed' : 'pointer', 
                fontSize: '1rem', 
                fontWeight: 500 
              }}
            >
              {isSubmitting ? 'Registrazione...' : 'Registra Disponibilità'}
            </button>
          </div>

        </div>
      </form>
    </div>
  )
}

export default AvailabilityForm
